import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtPct, fmtNum, fmtDate, monthLabel } from "@/lib/format";
import { PageHeader, SectionTitle, AwaitingData } from "@/components/common/Page";
import { KpiCard } from "@/components/common/KpiCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Archive, Landmark, Tags } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { data: c, isLoading } = useQuery({ queryKey: ["customer", id], queryFn: async () => (await api.get(`/customers/${id}`)).data });
  const archive = useMutation({
    mutationFn: async () => api.delete(`/customers/${id}`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Customer archived"); navigate("/customers"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;
  const p = c.profitability;

  return (
    <div className="ns-fade">
      <Button variant="ghost" className="mb-3 text-slate-400 hover:text-slate-100" onClick={() => navigate("/customers")} data-testid="back-to-customers">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Customers
      </Button>
      <PageHeader title={c.legal_name} subtitle={c.trading_name || "Customer profile & profitability"} testId="customer-detail-header">
        <StatusBadge status={c.status} />
        {c.founding_customer && <StatusBadge status="pilot" label="Founding Customer" />}
        <Button variant="outline" size="sm" className="border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10" onClick={() => setConfirmOpen(true)} data-testid="delete-customer-button">
          <Archive className="mr-1.5 h-4 w-4" /> Archive
        </Button>
      </PageHeader>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="archive-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {c.legal_name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This removes the customer from the active registry. Financial history and audit records are preserved — this is a soft-delete, not a permanent deletion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2D3F62] bg-transparent text-slate-300 hover:bg-[#1E2D4A]" data-testid="archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archive.mutate()} className="bg-rose-600 text-white hover:bg-rose-500" data-testid="archive-confirm">Archive customer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
            <SectionTitle className="mb-4">Profile</SectionTitle>
            <Field l="ABN" v={c.abn} />
            <Field l="Primary Contact" v={c.primary_contact} />
            <Field l="Email" v={c.email} />
            <Field l="Phone" v={c.phone} />
            <Field l="Vessels" v={fmtNum(c.vessels)} />
            <Field l="Users" v={fmtNum(c.users)} />
            <Field l="Billing Cycle" v={c.billing_cycle} />
            <Field l="Subscription" v={fmtMoney(c.subscription_value)} />
            <Field l="Start Date" v={fmtDate(c.start_date)} />
          </div>
          <CustomerBilling c={c} id={id} />
          <CustomerPackage c={c} id={id} />
          {c.founding_customer && (
            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-5">
              <SectionTitle className="mb-3">Founding Customer Arrangement</SectionTitle>
              <Field l="Normal Value" v={fmtMoney(c.normal_value)} />
              <Field l="Charged Value" v={fmtMoney(c.subscription_value)} />
              <Field l="Discount" v={fmtMoney((c.normal_value || 0) - (c.subscription_value || 0))} />
              <Field l="Discount %" v={c.normal_value ? fmtPct(((c.normal_value - c.subscription_value) / c.normal_value) * 100) : "—"} />
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <SectionTitle>Profitability · {p ? monthLabel(p.month) : ""}</SectionTitle>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard testId="cd-revenue" label="Revenue" value={fmtMoney(p?.revenue)} accent="cyan" />
            <KpiCard testId="cd-cost" label="Cost to Serve" value={fmtMoney(p?.cost_to_serve)} accent="amber" />
            <KpiCard testId="cd-contribution" label="Contribution" value={fmtMoney(p?.contribution)} accent={p?.contribution >= 0 ? "emerald" : "rose"} />
            <KpiCard testId="cd-margin" label="Margin" value={fmtPct(p?.margin_pct)} accent={p?.margin_pct >= 0 ? "emerald" : "rose"} />
          </div>

          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
            <SectionTitle className="mb-4">Cost-to-Serve Breakdown</SectionTitle>
            {(!p || p.cost_components.length === 0) ? (
              <AwaitingData label="No cost components — tag expenses or log labour to this customer" />
            ) : (
              <div className="space-y-2">
                {p.cost_components.map((comp, i) => (
                  <div key={i} className="flex items-start justify-between rounded-lg border border-[#1E2D4A] bg-[#17233B]/50 p-3">
                    <div>
                      <div className="text-sm text-slate-200">{comp.label}</div>
                      <div className="text-[11px] text-slate-500">Source: {comp.source}</div>
                    </div>
                    <span className="font-mono-fin text-sm font-semibold text-slate-100">{fmtMoney(comp.amount)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-[#1E2D4A] px-3 pt-3">
                  <span className="text-sm font-semibold text-slate-300">Total Cost to Serve</span>
                  <span className="font-mono-fin font-bold text-slate-50">{fmtMoney(p.cost_to_serve)}</span>
                </div>
              </div>
            )}
          </div>
          {c.notes && (
            <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
              <SectionTitle className="mb-2">Notes</SectionTitle>
              <p className="text-sm text-slate-400">{c.notes}</p>
            </div>
          )}
          <CustomerLegal customerId={id} />
          <CustomerInvoices customerId={id} />
        </div>
      </div>
    </div>
  );
}

const CustomerInvoices = ({ customerId }) => {
  const navigate = useNavigate();
  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const { data } = useQuery({
    queryKey: ["customer-invoices", customerId],
    queryFn: async () => (await api.get(`/invoices/customer/${customerId}/summary`)).data,
  });
  return (
    <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5" data-testid="customer-invoices-section">
      <div className="mb-4 flex items-center justify-between">
        <SectionTitle>Invoices</SectionTitle>
        <Button size="sm" variant="outline" className="border-[#2D3F62] bg-transparent text-cyan-300 hover:bg-[#17233B]" onClick={() => navigate("/invoices/new")} data-testid="customer-new-invoice">New Invoice</Button>
      </div>
      <div className="mb-3 grid grid-cols-4 gap-2 text-center text-xs">
        <div className="rounded bg-[#0B1120] py-2"><div className="text-slate-500">Invoiced</div><div className="text-slate-200">{money(data?.invoiced_cents)}</div></div>
        <div className="rounded bg-[#0B1120] py-2"><div className="text-slate-500">Paid</div><div className="text-emerald-300">{money(data?.paid_cents)}</div></div>
        <div className="rounded bg-[#0B1120] py-2"><div className="text-slate-500">Outstanding</div><div className="text-amber-300">{money(data?.outstanding_cents)}</div></div>
        <div className="rounded bg-[#0B1120] py-2"><div className="text-slate-500">Overdue</div><div className="text-rose-300">{money(data?.overdue_cents)}</div></div>
      </div>
      <div className="space-y-1.5">
        {(!data || data.invoices.length === 0) && <AwaitingData label="No invoices yet" />}
        {(data?.invoices || []).slice(0, 6).map((iv) => (
          <button key={iv.id} onClick={() => navigate(`/invoices/${iv.id}`)} className="flex w-full items-center justify-between rounded-lg border border-[#1E2D4A] bg-[#17233B]/50 px-3 py-2 text-left hover:border-cyan-500/40" data-testid={`customer-invoice-${iv.id}`}>
            <span className="font-mono text-xs text-cyan-300">{iv.number}</span>
            <span className="text-xs text-slate-300">{money(iv.total_cents)} · {iv.overdue ? "Overdue" : iv.status}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const CustomerLegal = ({ customerId }) => {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["customer-legal", customerId],
    queryFn: async () => (await api.get(`/legal/customers/${customerId}/legal`)).data,
  });
  return (
    <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5" data-testid="customer-legal-section">
      <div className="mb-4 flex items-center justify-between">
        <SectionTitle>Contracts & Legal</SectionTitle>
        <Button size="sm" variant="outline" className="border-[#2D3F62] bg-transparent text-cyan-300 hover:bg-[#17233B]"
          onClick={() => navigate("/legal/contracts")} data-testid="customer-new-contract">Manage in Legal</Button>
      </div>
      {(!data || data.contracts.length === 0) ? (
        <AwaitingData label="No contracts yet — create one in Legal & Contracts" />
      ) : (
        <div className="space-y-2" data-testid="customer-contracts-list">
          {data.contracts.map((ct) => (
            <button key={ct.id} onClick={() => navigate(`/legal/contracts/${ct.id}`)}
              className="flex w-full items-center justify-between rounded-lg border border-[#1E2D4A] bg-[#17233B]/50 px-3 py-2.5 text-left hover:border-cyan-500/40"
              data-testid={`customer-contract-${ct.id}`}>
              <div>
                <div className="text-sm text-slate-100"><span className="font-mono text-xs text-cyan-300">{ct.contract_ref}</span> · {ct.contract_type}</div>
                <div className="text-[11px] text-slate-500">{ct.product_name || "—"}{ct.dates?.expiry ? ` · expires ${ct.dates.expiry}` : ""}</div>
              </div>
              <span className="text-xs text-slate-400">{ct.lifecycle_status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomerBilling = ({ c, id }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";
  const openEdit = () => {
    setF({
      billing_contact_name: c.billing_contact_name || "", billing_email: c.billing_email || "",
      billing_address: c.billing_address || "", abn: c.abn || "",
      preferred_payment_method: c.preferred_payment_method || "", payment_terms: c.payment_terms || "",
      billing_currency: c.billing_currency || "AUD", tax_treatment: c.tax_treatment || "taxable",
      gst_applicable: c.gst_applicable !== false, po_required: !!c.po_required,
      invoice_delivery: c.invoice_delivery || "email", account_status: c.account_status || "active",
      po_number: c.po_number || "", cost_centre: c.cost_centre || "", ap_email: c.ap_email || "",
      vendor_id: c.vendor_id || "", supplier_reference: c.supplier_reference || "", billing_notes: c.billing_notes || "",
    });
    setOpen(true);
  };
  const save = useMutation({
    mutationFn: async () => (await api.put(`/customers/${id}`, { ...c, ...f })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customer", id] }); setOpen(false); toast.success("Billing profile saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const T = (label, k, full) => (
    <div className={full ? "col-span-2" : ""}><Label className="text-xs text-slate-400">{label}</Label>
      <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`mt-1 ${inputCls}`} data-testid={`billing-${k}`} /></div>
  );
  const B = (label, k) => (
    <div><Label className="text-xs text-slate-400">{label}</Label>
      <Select value={f[k] ? "yes" : "no"} onValueChange={(v) => setF({ ...f, [k]: v === "yes" })}>
        <SelectTrigger className={`mt-1 ${inputCls}`} data-testid={`billing-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100"><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
      </Select></div>
  );
  return (
    <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5" data-testid="customer-billing-section">
      <div className="mb-3 flex items-center justify-between">
        <SectionTitle className="flex items-center gap-2"><Landmark className="h-4 w-4 text-cyan-400" /> Billing Profile</SectionTitle>
        <Button size="sm" variant="outline" className="border-[#2D3F62] bg-transparent text-cyan-300 hover:bg-[#17233B]" onClick={openEdit} data-testid="edit-billing-btn">Edit</Button>
      </div>
      <Field l="Billing contact" v={c.billing_contact_name} />
      <Field l="Billing email" v={c.billing_email} />
      <Field l="Preferred method" v={c.preferred_payment_method} />
      <Field l="Payment terms" v={c.payment_terms} />
      <Field l="Billing currency" v={c.billing_currency} />
      <Field l="GST applicable" v={c.gst_applicable === false ? "No" : "Yes"} />
      <Field l="PO required" v={c.po_required ? "Yes" : "No"} />
      <Field l="Account status" v={c.account_status} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="billing-modal">
          <DialogHeader><DialogTitle>Edit billing profile</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {T("Billing contact name", "billing_contact_name")}
            {T("Billing email", "billing_email")}
            {T("Billing address", "billing_address", true)}
            {T("ABN", "abn")}
            {T("Preferred payment method", "preferred_payment_method")}
            {T("Payment terms", "payment_terms")}
            {T("Billing currency", "billing_currency")}
            {T("Tax treatment", "tax_treatment")}
            {B("GST applicable", "gst_applicable")}
            {B("Purchase order required", "po_required")}
            {T("Invoice delivery", "invoice_delivery")}
            {T("Account status", "account_status")}
            {T("PO number", "po_number")}
            {T("Cost centre", "cost_centre")}
            {T("Accounts payable email", "ap_email")}
            {T("Vendor ID", "vendor_id")}
            {T("Supplier reference", "supplier_reference")}
            <div className="col-span-2"><Label className="text-xs text-slate-400">Billing notes</Label>
              <Textarea rows={2} value={f.billing_notes || ""} onChange={(e) => setF({ ...f, billing_notes: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="billing-notes" /></div>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending} className="mt-3 bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="save-billing-btn">Save billing profile</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ l, v }) => (
  <div className="flex items-center justify-between border-b border-[#1E2D4A] py-2 last:border-0">
    <span className="text-xs text-slate-500">{l}</span>
    <span className="text-sm capitalize text-slate-200">{v || "—"}</span>
  </div>
);

const CustomerPackage = ({ c, id }) => {
  const qc = useQueryClient();
  const [sel, setSel] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { data: packages } = useQuery({ queryKey: ["commercial-packages"], queryFn: async () => (await api.get("/commercial/packages")).data });
  const eligible = (packages || []).filter((p) => p.active && (c.products || []).includes(p.product_id));
  const current = (packages || []).find((p) => p.id === c.commercial_package_id);

  const assign = useMutation({
    mutationFn: async (pkgId) => (await api.put(`/commercial/customers/${id}/package`, { commercial_package_id: pkgId })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customer", id] }); setSel(""); toast.success("Commercial package assigned"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/commercial/customers/${id}/package`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customer", id] }); setConfirmOpen(false); toast.success("Commercial package removed"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";

  return (
    <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5" data-testid="customer-package-section">
      <SectionTitle className="mb-3 flex items-center gap-2"><Tags className="h-4 w-4 text-cyan-400" /> Commercial Package</SectionTitle>
      {current ? (
        <div>
          <Field l="Package" v={`${current.code} · ${current.name}`} />
          <Field l="Product" v={current.product_name} />
          <Field l="Base (ex GST)" v={current.base_price_ex_gst ? `$${current.base_price_ex_gst}` : "—"} />
          <Field l="Billing cycle" v={current.billing_cycle} />
          <p className="mt-2 text-[11px] text-slate-500">Negotiated customer price (subscription value) always overrides the package standard price.</p>
          <Button size="sm" variant="outline" className="mt-2 border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10" onClick={() => setConfirmOpen(true)} data-testid="remove-package-btn">Remove assignment</Button>
        </div>
      ) : (
        <div>
          <p className="mb-2 text-xs text-slate-500">No commercial package assigned. Legacy plan: <span className="text-slate-300">{c.subscription_plan || "—"}</span></p>
          {eligible.length === 0 ? (
            <p className="text-[11px] text-slate-500">No active package matches this customer's products. Create one under Commercial Packages.</p>
          ) : (
            <div className="flex items-center gap-2">
              <Select value={sel} onValueChange={setSel}>
                <SelectTrigger className={inputCls} data-testid="assign-package-select"><SelectValue placeholder="Select a package" /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                  {eligible.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" disabled={!sel || assign.isPending} onClick={() => assign.mutate(sel)} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="assign-package-btn">Assign</Button>
            </div>
          )}
        </div>
      )}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="remove-package-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove commercial package?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This clears the package assignment for {c.legal_name}. Issued invoices are unaffected. You can reassign a package at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2D3F62] bg-transparent text-slate-300 hover:bg-[#1E2D4A]" data-testid="remove-package-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate()} className="bg-rose-600 text-white hover:bg-rose-500" data-testid="remove-package-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
